import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Register from "@/pages/Register";
import MemberPortal from "@/pages/MemberPortal";
import Landing from "@/pages/Landing";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Members from "@/pages/Members";
import Donations from "@/pages/Donations";
import Ministries from "@/pages/Ministries";
import Attendance from "@/pages/Attendance";
import Trainings from "@/pages/Trainings";
import Team from "@/pages/Team";
import Finances from "@/pages/Finances";
import Families from "@/pages/Families";
import PrintDirectory from "@/pages/PrintDirectory";
import Announcements from "@/pages/Announcements";
import Messages from "@/pages/Messages";
import SiteSettings from "@/pages/SiteSettings";
import PendingApprovals from "@/pages/PendingApprovals";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF9F5]">
      <div className="h-10 w-10 rounded-full border-4 border-[#E5E0D8] border-t-[#2D5A27] animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user) return <Navigate to={user.role === "member" ? "/portal" : "/app"} replace />;
  return children;
}

function BoardOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "member") return <Navigate to="/portal" replace />;
  return children;
}

function FinanceOnly({ children }) {
  const { user } = useAuth();
  if (user?.access === "read_nonfin") return <Navigate to="/app" replace />;
  return children;
}

function PortalRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "member" && !user.member_id) return <Navigate to="/app" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/portal" element={<PortalRoute><MemberPortal /></PortalRoute>} />
      <Route path="/print/directory" element={<BoardOnly><PrintDirectory /></BoardOnly>} />
      <Route
        path="/app"
        element={
          <BoardOnly>
            <Layout />
          </BoardOnly>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="members" element={<Members />} />
        <Route path="approvals" element={<PendingApprovals />} />
        <Route path="donations" element={<FinanceOnly><Donations /></FinanceOnly>} />
        <Route path="ministries" element={<Ministries />} />
        <Route path="attendance" element={<Attendance />} />
        <Route path="finances" element={<FinanceOnly><Finances /></FinanceOnly>} />
        <Route path="families" element={<Families />} />
        <Route path="trainings" element={<Trainings />} />
        <Route path="announcements" element={<Announcements />} />
        <Route path="messages" element={<Messages />} />
        <Route path="settings" element={<SiteSettings />} />
        <Route path="team" element={<Team />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
